// frontend/src/context/AuthContext.jsx
// ============================================================
// PAWNI — Authentication Context
// Provides user state, login/signup/logout actions,
// and automatic token restoration on mount.
// ============================================================

import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authApi, setTokens, clearTokens, getAccessToken } from "../utils/api";
import { STORAGE_KEYS } from "../config";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Restore session on mount ─────────────────────────────

  useEffect(() => {
    const restoreSession = async () => {
      const token = getAccessToken();
      const savedUser = localStorage.getItem(STORAGE_KEYS.user);

      if (!token) {
        setLoading(false);
        return;
      }

      // Try to use saved user data first (faster)
      if (savedUser) {
        try {
          setUser(JSON.parse(savedUser));
        } catch {
          // Corrupted data, ignore
        }
      }

      // Verify token is still valid
      try {
        const profile = await authApi.getMe();
        setUser(profile);
        localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(profile));
      } catch {
        // Token expired or invalid
        clearTokens();
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    restoreSession();
  }, []);

  // ── Listen for auth expiry events ────────────────────────

  useEffect(() => {
    const handleExpiry = () => {
      setUser(null);
      clearTokens();
    };

    window.addEventListener("pawni:auth-expired", handleExpiry);
    return () => window.removeEventListener("pawni:auth-expired", handleExpiry);
  }, []);

  // ── Actions ──────────────────────────────────────────────

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const data = await authApi.login(email, password);
      setTokens(data.access_token, data.refresh_token);
      setUser(data.user);
      localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(data.user));
      return data.user;
    } catch (err) {
      const msg = typeof err?.message === "string" ? err.message : "Login failed.";
      setError(msg);
      throw err;
    }
  }, []);

  const signup = useCallback(async (email, password, displayName, secretCode) => {
    setError(null);
    try {
      const data = await authApi.signup(email, password, displayName, secretCode);
      if (data.access_token) {
        setTokens(data.access_token, data.refresh_token);
        setUser(data.user);
        localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(data.user));
      }
      return data.user;
    } catch (err) {
      const msg = typeof err?.message === "string" ? err.message : "Signup failed.";
      setError(msg);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors
    } finally {
      clearTokens();
      setUser(null);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  // ── Context Value ────────────────────────────────────────

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    signup,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
